import { useMutation, useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import {
  Text,
  GreyGrey10,
  IconSquareBehindSquare,
  IndigoIndigo60,
  Link,
  Popover,
  Primary13Regular,
  SettingsFormControl,
  SettingsGroup,
  Switch,
  TextOverflow,
  Tooltip,
  useNotifications,
  Box,
  Flex
} from '@sequencehq/core-components'
import { automationsPaths, useIsAutomationActive } from 'Automations/index'

export const ContractForwardingSettings = () => {
  const { displayNotification } = useNotifications()

  const agentsSettingsQuery = useQuery(
    dashboardv99990101Client.getAgentsSettings,
    undefined,
    {
      retry: (failureCount, error) => {
        if (error.status === 404) return false
        if (failureCount === 3) return false
        return true
      }
    }
  )

  const enableAgentSettingsMutation = useMutation(
    dashboardv99990101Client.postAgentsEnable,
    {
      onSuccess: () => {
        displayNotification('Contract forwarding enabled', { type: 'success' })
      },
      onError: () => {
        displayNotification('Error enabling contract forwarding', {
          type: 'error'
        })
      },
      onMutate: () => {
        displayNotification('Enabling contract forwarding...', { type: 'info' })
      }
    }
  )

  const disableAgentSettingsMutation = useMutation(
    dashboardv99990101Client.deleteAgentsDisable,
    {
      onSuccess: () => {
        displayNotification('Contract forwarding disabled', { type: 'success' })
      },
      onError: () => {
        displayNotification('Error disabling contract forwarding', {
          type: 'error'
        })
      },
      onMutate: () => {
        displayNotification('Disabling contract forwarding...', {
          type: 'info'
        })
      }
    }
  )

  const { isActive: isAutomationActive, isPending: isAutomationPending } =
    useIsAutomationActive({
      triggerType: 'CONTRACT_UPLOADED'
    })

  const handleEnableForwarding = async () => {
    await enableAgentSettingsMutation.mutateAsync()
  }

  const handleDisableForwarding = async () => {
    await disableAgentSettingsMutation.mutateAsync()
  }

  const handleCopyEmail = async () => {
    if (!agentsSettingsQuery.data?.email) return

    try {
      await navigator.clipboard.writeText(agentsSettingsQuery.data.email)
      displayNotification('Forwarding email copied to clipboard', {
        type: 'success'
      })
    } catch (err) {
      console.error('Failed to copy email to clipboard:', err)
      displayNotification('Failed to copy email address', { type: 'error' })
    }
  }

  const emailForwardingEnabled =
    !!agentsSettingsQuery.data &&
    !agentsSettingsQuery.error &&
    isAutomationActive

  return (
    <SettingsGroup
      isLoading={agentsSettingsQuery.isLoading}
      title="Contract forwarding"
      footer="AI extracts details from forwarded contracts. Max 5 PDFs per email."
    >
      <SettingsFormControl label="Forwarding email" htmlFor="forwarding-email">
        <Popover
          isDisabled={isAutomationActive || isAutomationPending}
          trigger="hover"
          content={
            <Box {...Primary13Regular}>
              <Link.Unstyled
                to={automationsPaths.automationsList()}
                style={{ color: IndigoIndigo60 }}
              >
                Set up a contract intake automation
              </Link.Unstyled>{' '}
              to start uploading contracts.
            </Box>
          }
        >
          <Switch
            data-testid="contractForwarding.switch"
            id="forwarding-email"
            as="div"
            size="md"
            disabled={
              enableAgentSettingsMutation.isPending ||
              disableAgentSettingsMutation.isPending ||
              !isAutomationActive
            }
            defaultChecked={emailForwardingEnabled}
            onChange={(value: boolean) => {
              if (value) {
                handleEnableForwarding()
                return
              }
              handleDisableForwarding()
            }}
          />
        </Popover>
      </SettingsFormControl>

      {emailForwardingEnabled && (
        <Box paddingTop="16px" paddingBottom="16px">
          <Flex
            data-testid="contractForwarding.email"
            backgroundColor={GreyGrey10}
            borderRadius="8px"
            padding="8px 12px"
            justifyContent="space-between"
            alignItems="center"
            position="relative"
          >
            <Text {...Primary13Regular} flex={1} overflow="hidden">
              <TextOverflow splitPercentage={75}>
                {agentsSettingsQuery.data?.email ?? ''}
              </TextOverflow>
            </Text>
            <Tooltip label="Copy to clipboard" placement="top">
              <Box
                data-testid="contractForwarding.copyEmail"
                as="button"
                onClick={handleCopyEmail}
                background="none"
                border="none"
                cursor="pointer"
                padding="4px"
                display="flex"
                alignItems="center"
              >
                <IconSquareBehindSquare />
              </Box>
            </Tooltip>
          </Flex>
        </Box>
      )}
    </SettingsGroup>
  )
}
